import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { sendRequestAsync } from "../api/requestService";
import { httpMethod, requestStatus, getMessageListUrl, submitNewAnswerUrl, getAllUserReportsByHumanIdUrl, markAnswerAsReadUrl, changeReportStatusUrl, downloadMessageAttachmentUrl } from "../api/apiConsts";
import { getFormatedDateTimeString } from '../helpers/dateTimeHelper';
import { setErrorWithoutRedirect } from "../reducers/loadingSlice";
import store from '../store';
import { authHelper } from '../api/authHelper';
import { answerSource } from '../consts';

export const changeReportStatus = createAsyncThunk("message/changeReportStatus", async (reportId) => {
    const response = await sendRequestAsync(httpMethod.post, changeReportStatusUrl, { 
        reportIds: [reportId],
        shouldBeDeleted: false
     });
    return response.data;
})

export const fetchUserReportsByHumanId = createAsyncThunk("message/fetchUserReportsByHumanId", async (humanId) => {
    const response = await sendRequestAsync(httpMethod.get, `${getAllUserReportsByHumanIdUrl}?humanId=${humanId}&orderDesc=true`);
    return response.data;
})

export const fetchMessagesList = createAsyncThunk("message/fetchMessagesList", async ({ pin, number }) => {
    let url = `${getMessageListUrl}?id=${number}&pin=${pin}&orderAsc=true`;
    if (!pin && (authHelper.getUserHumanId() || authHelper.getUserVideoChatToken())) {
        url = `${getMessageListUrl}?id=${number}&pin=${pin}&orderAsc=true&withoutPin=true`;
    }
    const response = await sendRequestAsync(httpMethod.get, url);

    if (response?.status === 200) {
      await sendRequestAsync(httpMethod.post, markAnswerAsReadUrl, { reportId: response?.data?.ReportId });
    }

    return response.data;
})

export const submitAnswer = createAsyncThunk("message/submitAnswer", async ({ answerText, files  }, { getState }) => {
    const { message } = getState();
    const formData = new FormData();
        formData.append("ReportId", message.chat.ReportId);
        formData.append("AnswerText", answerText);
        formData.append("AnswerSource", answerSource.client);
        formData.append("AllowAnswerFromClient", message.chat.AllowAnswerFromClient);
        formData.append("OrderAsc", true);

        if (files && files.length > 0) {
            files.forEach(f => {
                formData.append("Files", f);
            });
    }
    const response = await sendRequestAsync(httpMethod.post, submitNewAnswerUrl, formData, (errorMessage, error) => {
        const filesSizeErrorMessage = "The total size of uploaded files must not exceed 30 MB.";
        const responseStatus = error?.response?.status;
        const requestError = error?.response?.data?.errors?.[""]?.[0];
        const serverMessage = error?.response?.data?.Message || error?.response?.data?.title;
        const isFilesSizeExceeded = responseStatus === 413
            || (responseStatus === 400
            && typeof requestError === "string"
            && requestError.toLowerCase().includes("request body too large"))
            || ((responseStatus === 400 || responseStatus === 413)
            && typeof serverMessage === "string"
            && serverMessage.toLowerCase().includes("too large"));

        if (isFilesSizeExceeded) {
            store.dispatch(setErrorWithoutRedirect(filesSizeErrorMessage));
            return;
        }

        store.dispatch(setErrorWithoutRedirect(errorMessage));
    });

    if (!response?.data) {
        throw new Error("Submit answer request failed");
    }

    return response.data;
},
    {
        condition: (args, { getState }) => {
            const { message } = getState();
            return message?.chat?.ReportId;
        }
    })

export const fetchMessageAttachmentObjectUrl = createAsyncThunk("message/fetchMessageAttachmentObjectUrl", async (attachmentId) => {
    const response = await axios({
        method: httpMethod.get,
        url: `${downloadMessageAttachmentUrl}?attachmentId=${attachmentId}`,
        responseType: "blob"
    });

    return response?.data ? URL.createObjectURL(response.data) : null;
})

export const messageSlice = createSlice({
    name: "message",
    initialState: {
        chat: null,
        credentials: null,
        status: requestStatus.idle
    },
    reducers: {
        setCredentials: (state, action) => {
            state.credentials = action.payload;
        },
        clearState: (state, action) => {
            state.chat = null;
            state.credentials = null;
            state.status = requestStatus.idle;
        },
        addMessage: (state, action) => {
            state.chat.Answers.push({ ...action.payload, AnsweredAt: getFormatedDateTimeString(action.payload.AnsweredAt) })
        }
    },
    extraReducers: builder => {
        builder.addCase(fetchUserReportsByHumanId.fulfilled, (state, action) => {
            const reports = action.payload;
            state.chat = { ...action.payload, Reports: reports.map(r => ({...r, ReportSubmittedAt: getFormatedDateTimeString(r.ReportSubmittedAt), Answers: r.Answers.map(a => ({...a, AnsweredAt: getFormatedDateTimeString(a.AnsweredAt)}))})) };
            state.status = requestStatus.succeeded;
        })
        builder.addCase(fetchUserReportsByHumanId.pending, (state, action) => {
            state.status = requestStatus.loading
        })
        builder.addCase(fetchUserReportsByHumanId.rejected, (state, action) => {
            state.status = requestStatus.failed;
        })
        builder.addCase(fetchMessagesList.fulfilled, (state, action) => {
            const answers = action.payload.Answers.map(x => ({ ...x, AnsweredAt: getFormatedDateTimeString(x.AnsweredAt)}));
            state.chat = { ...action.payload, Answers: answers };
            state.status = requestStatus.succeeded;
        })
        builder.addCase(fetchMessagesList.pending, (state, action) => {
            state.status = requestStatus.loading
        })
        builder.addCase(fetchMessagesList.rejected, (state, action) => {
            state.status = requestStatus.failed;
        })
        builder.addCase(submitAnswer.fulfilled, (state, action) => {
            const answers = action.payload.Answers.map(x => ({ ...x, AnsweredAt: getFormatedDateTimeString(x.AnsweredAt) }));
            state.chat = { ...action.payload, Answers: answers };
            state.status = requestStatus.succeeded;
        })
        builder.addCase(submitAnswer.pending, (state, action) => {
            state.status = requestStatus.loading
        })
        builder.addCase(submitAnswer.rejected, (state, action) => {
            state.status = requestStatus.failed;
        })
    }
})

export const { setCredentials, addMessage, clearState } = messageSlice.actions;

export default messageSlice.reducer;